/******
 * This file was generated by a Rake task.
 * Do not modify this file manually.
 * Add changes to the `Permission.ts` file.
 ******/
// oxlint-disable-next-line no-unused-vars
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

import { Permission } from "./Permission";

import { Audit } from "./Audit";
import { Project } from "./Project";
import { Team } from "./Team";
import { User } from "./User";

export type permission =
    | "is_invited"
    | "is_subscribed"
    | "is_member"
    | "is_lead"
    | "is_owner"
    | "is_plot_admin"
    | "is_admin"
    | "is_vendor"
    | "is_final_approver";

export const permissionValues = {
    is_invited: 0,
    is_subscribed: 1,
    is_member: 2,
    is_lead: 3,
    is_owner: 4,
    is_plot_admin: 5,
    is_admin: 6,
    is_vendor: 8,
    is_final_approver: 9,
};

export abstract class PermissionModel {
    id: number;
    createdAt: Dayjs;
    permission: permission;
    projectId: number | null;
    teamId: number | null;
    updatedAt: Dayjs;
    userId: number | null;
    audits: Audit[] | undefined;
    project: Project | null | undefined;
    team: Team | null | undefined;
    user: User | null | undefined;
    _destroy: boolean | undefined;

    /* eslint-disable max-statements */
    constructor(
        id: number,
        createdAt: Dayjs,
        permission: permission,
        projectId: number | null,
        teamId: number | null,
        updatedAt: Dayjs,
        userId: number | null,
        audits: Audit[] | undefined,
        project: Project | null | undefined,
        team: Team | null | undefined,
        user: User | null | undefined
    ) {
        this.id = id;
        this.createdAt = createdAt;
        this.permission = permission;
        this.projectId = projectId;
        this.teamId = teamId;
        this.updatedAt = updatedAt;
        this.userId = userId;
        this.audits = audits;
        this.project = project;
        this.team = team;
        this.user = user;
    }
    /* eslint-enable max-statements */

    // oxlint-disable-next-line complexity
    static fromApiObject(json: any, timezone: string): Permission {
        const newObject = new Permission(
            json["id"],
            dayjs(json["created_at"]).tz(timezone),
            json["permission"],
            json["project_id"],
            json["team_id"],
            dayjs(json["updated_at"]).tz(timezone),
            json["user_id"],
            json["audits"]?.map((item: any) => Audit.fromApiObject(item, timezone)),
            json["project"] == null ? json["project"] : Project.fromApiObject(json["project"], timezone),
            json["team"] == null ? json["team"] : Team.fromApiObject(json["team"], timezone),
            json["user"] == null ? json["user"] : User.fromApiObject(json["user"], timezone)
        );

        return newObject;
    }

    // oxlint-disable-next-line complexity
    toApiObject(visited = new Set<any>()): any {
        if (visited.has(this)) {
            // Return null, undefined, or primary key to break the cycle
            return undefined;
        }
        visited.add(this);

        return {
            id: this.id,
            created_at: this.createdAt,
            permission: this.permission,
            project_id: this.projectId,
            team_id: this.teamId,
            updated_at: this.updatedAt,
            user_id: this.userId,
            audits: this.audits?.map((item) => item.toApiObject(visited)).filter((item) => item !== undefined),
            project_attributes: this.project?.toApiObject(visited),
            team_attributes: this.team?.toApiObject(visited),
            user_attributes: this.user?.toApiObject(visited),
            _destroy: this._destroy,
        };
    }

    // NOTE: This could potentially cause infinite loops if the object graph is
    // circular.
    inAlternateTimezone(timezone: string): Permission {
        const clone = Object.create(this) as Permission;

        for (const key of Object.keys(this)) {
            if (this[key] instanceof dayjs) {
                // @ts-expect-error: Typescript thinks that this is not a dayjs object even though it is checked on the line before
                clone[key] = this[key].tz(timezone);
            } else if (Array.isArray(this[key])) {
                clone[key] = this[key].map((item) =>
                    item.inAlternateTimezone ? item.inAlternateTimezone(timezone) : item
                );
            } else if (this[key] && this[key].inAlternateTimezone) {
                clone[key] = this[key].inAlternateTimezone(timezone);
            }
        }

        return clone;
    }
}
